import { alibabaModels } from "./models/alibaba.js";
import { anthropicModels } from "./models/anthropic.js";
import { atlascloudModels } from "./models/atlascloud.js";
import { atriaModels } from "./models/atria.js";
import { baaiModels } from "./models/baai.js";
import { baiduModels } from "./models/baidu.js";
import { blackForestLabsModels } from "./models/black-forest-labs.js";
import { bytedanceModels } from "./models/bytedance.js";
import { deepseekModels } from "./models/deepseek.js";
import { elevenlabsModels } from "./models/elevenlabs.js";
import { googleModels } from "./models/google.js";
import { ibmModels } from "./models/ibm.js";
import { inclusionaiModels } from "./models/inclusionai.js";
import { kinfraModels } from "./models/kinfra.js";
import { llmgatewayModels } from "./models/llmgateway.js";
import { metaModels } from "./models/meta.js";
import { microsoftModels } from "./models/microsoft.js";
import { minimaxModels } from "./models/minimax.js";
import { mistralModels } from "./models/mistral.js";
import { moonshotModels } from "./models/moonshot.js";
import { nousresearchModels } from "./models/nousresearch.js";
import { nvidiaModels } from "./models/nvidia.js";
import { openaiModels } from "./models/openai.js";
import { openbmbModels } from "./models/openbmb.js";
import { perplexityModels } from "./models/perplexity.js";
import { reveModels } from "./models/reve.js";
import { sakanaModels } from "./models/sakana.js";
import { stepfunModels } from "./models/stepfun.js";
import { tencentModels } from "./models/tencent.js";
import { thinkingmachinesModels } from "./models/thinkingmachines.js";
import { typesafeModels } from "./models/typesafe.js";
import { xaiModels } from "./models/xai.js";
import { xiaomiModels } from "./models/xiaomi.js";
import { zaiModels } from "./models/zai.js";

import type { providers } from "./providers.js";

export type Provider = (typeof providers)[number]["id"];

export type Model = (typeof models)[number]["id"];

/**
 * Decimal-safe price representation. Always a string so values are preserved
 * exactly (no IEEE-754 noise) all the way from model definition through to
 * the Decimal-based cost engine.
 */
export type Price = string;

/**
 * Reasoning effort tiers accepted by the unified reasoning_effort parameter,
 * in ascending order of effort. Which subset a given provider mapping
 * actually supports is declared per mapping via `reasoningEfforts`.
 */
export const REASONING_EFFORTS = [
	"none",
	"minimal",
	"low",
	"medium",
	"high",
	"xhigh",
	"max",
] as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

/**
 * Execution strategy accepted by the unified `reasoning.mode` parameter.
 * `pro` spends additional model work on hard problems, at higher latency and
 * token usage. Orthogonal to `reasoning_effort`, which controls how much
 * reasoning happens within the selected mode. Which subset a given provider
 * mapping supports is declared per mapping via `reasoningModes`.
 */
export type ReasoningMode = "standard" | "pro";

export const PROVIDER_API_FORMATS = [
	"provider-native",
	"openai-chat-completions",
	"openai-responses",
	"google-vertex",
] as const;

export type ProviderApiFormat = (typeof PROVIDER_API_FORMATS)[number];

/**
 * API surfaces a single request can be sent on: the mapping formats plus
 * `anthropic-messages`, which only `webSearchApiFormat` selects.
 */
export type RequestApiFormat = ProviderApiFormat | "anthropic-messages";

/**
 * Pricing tier for models with context-length based pricing
 */
export interface PricingTier {
	/**
	 * Name of the pricing tier (e.g., "128K", "1M")
	 */
	name: string;
	/**
	 * Maximum number of tokens for this tier (use Infinity for the highest tier)
	 */
	upToTokens: number;
	/**
	 * Price per input token in USD for this tier
	 */
	inputPrice: Price;
	/**
	 * Price per output token in USD for this tier
	 */
	outputPrice: Price;
	/**
	 * Price per cached input token in USD for this tier.
	 * Used when the cache hit was NOT explicitly requested by the caller. For
	 * Alibaba this is the implicit-cache rate (20% of input); for Anthropic this
	 * is the explicit-cache read rate (10%) since Anthropic only has explicit
	 * caching; for OpenAI this is the automatic prompt-cache rate.
	 */
	cachedInputPrice?: Price;
	/**
	 * Price per cached input token when the request used `cache_control` to
	 * explicitly mark content for caching (provider-specific explicit-cache hit
	 * rate). When unset, falls back to `cachedInputPrice`. Currently only set on
	 * Alibaba Qwen, where explicit hits bill at 10% vs. implicit at 20%.
	 */
	cacheReadInputPrice?: Price;
	/**
	 * Price per cache write input token in USD for this tier (5-minute TTL).
	 * For Anthropic, this is the 1.25x base-input rate.
	 */
	cacheWriteInputPrice?: Price;
	/**
	 * Price per cache write input token in USD for this tier (1-hour TTL).
	 * For Anthropic, this is the 2x base-input rate. When unset, 1-hour writes
	 * fall back to `cacheWriteInputPrice` (the 5-minute rate).
	 */
	cacheWriteInputPrice1h?: Price;
	/**
	 * Peak/off-peak rates for this tier, for providers that price each
	 * context-length band by time of day. The schedule comes from the
	 * mapping's (or region's) `peakPricing`, which MUST be set; without this
	 * block the tier bills its flat rates at every hour.
	 */
	peakPricing?: Pick<PeakPricing, "peak" | "offPeak">;
}

/**
 * Peak/off-peak time-of-day rates. `peak` applies while the current UTC hour
 * falls inside `hoursUtc`; every other hour is off-peak.
 */
export interface PeakPricing {
	/**
	 * Prices charged during peak hours.
	 */
	peak: {
		/**
		 * Price per input token in USD during peak hours.
		 */
		inputPrice: Price;
		/**
		 * Price per output token in USD during peak hours.
		 */
		outputPrice: Price;
		/**
		 * Price per cached input token in USD during peak hours. When
		 * unset, billing falls back to `inputPrice`, matching base-price
		 * behavior.
		 */
		cachedInputPrice?: Price;
	};
	/**
	 * Prices charged during off-peak hours.
	 */
	offPeak: {
		/**
		 * Price per input token in USD during off-peak hours.
		 */
		inputPrice: Price;
		/**
		 * Price per output token in USD during off-peak hours.
		 */
		outputPrice: Price;
		/**
		 * Price per cached input token in USD during off-peak hours. When
		 * unset, billing falls back to `inputPrice`, matching base-price
		 * behavior.
		 */
		cachedInputPrice?: Price;
	};
	/**
	 * Peak hours in UTC as half-open [start, end) hour ranges (0-23). All
	 * hours outside these ranges are off-peak.
	 */
	hoursUtc: readonly [start: number, end: number][];
	/**
	 * UTC days of the week that are always billed off-peak, using JavaScript's
	 * numbering (Sunday = 0, Saturday = 6).
	 */
	offPeakDaysUtc?: readonly number[];
}

/**
 * Pricing and availability for a specific geographic region.
 * When defined on a ProviderModelMapping, the first entry is the default region.
 * Top-level inputPrice/outputPrice always reflect the default (first) region
 * for backwards compatibility.
 */
export interface ProviderRegion {
	/**
	 * Region identifier (e.g. "singapore", "us-virginia", "cn-beijing")
	 */
	id: string;
	/**
	 * Price per input token in USD for this region.
	 * When absent, falls back to the mapping-level inputPrice.
	 */
	inputPrice?: Price;
	/**
	 * Price per output token in USD for this region.
	 * When absent, falls back to the mapping-level outputPrice.
	 */
	outputPrice?: Price;
	/**
	 * Price per cached input token in USD for this region
	 */
	cachedInputPrice?: Price;
	/**
	 * Price per cached input token when the request used `cache_control` to
	 * explicitly mark content for caching. When unset, falls back to
	 * `cachedInputPrice`. See PricingTier docs.
	 */
	cacheReadInputPrice?: Price;
	/**
	 * Price per cache write input token in USD for this region (5-minute TTL)
	 */
	cacheWriteInputPrice?: Price;
	/**
	 * Price per cache write input token in USD for this region (1-hour TTL).
	 * When unset, 1-hour writes fall back to `cacheWriteInputPrice`.
	 */
	cacheWriteInputPrice1h?: Price;
	/**
	 * Context-length based pricing tiers for this region.
	 * When absent, falls back to the mapping-level pricingTiers.
	 */
	pricingTiers?: PricingTier[];
	/**
	 * Peak/off-peak rates for this region. A mapping that prices peak hours
	 * per region MUST set this on every region: the region-level price
	 * overrides above are ignored while a peakPricing block is in effect, so a
	 * region that only overrides `inputPrice` would silently bill at the
	 * mapping-level peak rates.
	 */
	peakPricing?: PeakPricing;
	/**
	 * Price per request in USD for this region.
	 * When absent, falls back to the mapping-level requestPrice.
	 */
	requestPrice?: Price;
	/**
	 * Price per web search query in USD for this region.
	 * When absent, falls back to the mapping-level webSearchPrice.
	 */
	webSearchPrice?: Price;
	/**
	 * Context window size in tokens for this region.
	 * When absent, falls back to the mapping-level contextSize.
	 */
	contextSize?: number;
	/**
	 * Maximum output size in tokens for this region.
	 * When absent, falls back to the mapping-level maxOutput.
	 */
	maxOutput?: number;
	/**
	 * Streaming support override for this region.
	 * When absent, falls back to the mapping-level streaming.
	 */
	streaming?: boolean | "only";
	/**
	 * Test skip/only for this specific region.
	 * When absent, falls back to the mapping-level test.
	 */
	test?: "skip" | "only";
}

/**
 * The distinct `tool_choice` modes a provider/model mapping may accept.
 * "function" represents a named function choice (`{type:"function",...}`).
 */
export const TOOL_CHOICE_MODES = [
	"auto",
	"none",
	"required",
	"function",
] as const;

export type ToolChoiceMode = (typeof TOOL_CHOICE_MODES)[number];

/**
 * How an OpenAI-compatible Chat Completions endpoint takes a web search
 * request. "web_search_options" is the Chat Completions body field;
 * "web_search_tool" is a `tools: [{type: "web_search"}]` entry (the Responses
 * API's hosted tool), which some chat endpoints accept instead.
 */
export const WEB_SEARCH_FORMATS = [
	"web_search_options",
	"web_search_tool",
] as const;

export type WebSearchFormat = (typeof WEB_SEARCH_FORMATS)[number];

export interface ProviderModelMapping {
	providerId: (typeof providers)[number]["id"];
	/**
	 * Provider-specific upstream model id used when calling the upstream
	 * provider. Distinct from the canonical `ModelDefinition.id` and from any
	 * human-readable display name.
	 */
	externalId: string;
	/**
	 * Price per input token in USD
	 */
	inputPrice?: Price;
	/**
	 * Price per output token in USD
	 */
	outputPrice?: Price;
	/**
	 * Price per output audio token in USD (for speech generation / text-to-speech
	 * models where audio output is billed separately from text). When unset,
	 * audio output tokens fall back to `outputPrice`.
	 */
	outputAudioPrice?: Price;
	/**
	 * Price per input character in USD. Used by speech generation models that
	 * bill on input characters rather than tokens (e.g. OpenAI `tts-1`), since
	 * the OpenAI speech endpoint returns audio bytes without token usage.
	 */
	inputCharacterPrice?: Price;
	/**
	 * Price per hour of input audio in USD. Used by transcription
	 * (speech-to-text) models that bill on audio duration rather than tokens
	 * (e.g. xAI STT at $0.10/hour). Cost is computed against the `duration`
	 * (seconds) reported by the upstream transcription response.
	 */
	inputAudioHourPrice?: Price;
	/**
	 * Price per user text input item in USD. Used by realtime speech-to-speech
	 * models that bill session time plus a flat fee for each text
	 * message the caller adds to the conversation (e.g. xAI Grok Voice).
	 */
	inputTextItemPrice?: Price;
	/**
	 * Price per image output token in USD (for models with separate text/image output pricing)
	 */
	imageOutputPrice?: Price;
	/**
	 * Price per cached input token in USD.
	 * Used when the cache hit was NOT explicitly requested by the caller. For
	 * Alibaba this is the implicit-cache rate (20% of input); for Anthropic this
	 * is the explicit-cache read rate (10%) since Anthropic only has explicit
	 * caching; for OpenAI this is the automatic prompt-cache rate.
	 */
	cachedInputPrice?: Price;
	/**
	 * Price per cached input token when the request used `cache_control` to
	 * explicitly mark content for caching (provider-specific explicit-cache hit
	 * rate). When unset, falls back to `cachedInputPrice`. Currently only set on
	 * Alibaba Qwen, where explicit hits bill at 10% vs. implicit at 20%.
	 */
	cacheReadInputPrice?: Price;
	/**
	 * Price per cache write input token in USD (5-minute TTL).
	 * For Anthropic, this is the 1.25x base-input rate.
	 */
	cacheWriteInputPrice?: Price;
	/**
	 * Price per cache write input token in USD (1-hour TTL).
	 * For Anthropic, this is the 2x base-input rate. When unset, 1-hour writes
	 * fall back to `cacheWriteInputPrice` (the 5-minute rate).
	 */
	cacheWriteInputPrice1h?: Price;
	/**
	 * Minimum number of tokens required for a segment to be cacheable.
	 * Prompts smaller than this threshold won't be cached even with cache_control set.
	 * Model-specific: Claude 3 Haiku requires 2048, Claude Opus 4.5 requires 4096, most others require 1024.
	 */
	minCacheableTokens?: number;
	/**
	 * Price per image input in USD
	 */
	imageInputPrice?: Price;
	/**
	 * Price per audio input token in USD. When unset, audio input tokens are
	 * billed at the regular `inputPrice` (used for providers that don't price
	 * audio separately, e.g. Gemini 2.5 Pro where audio follows the text tier).
	 */
	inputAudioPrice?: Price;
	/**
	 * Price per cached image input token in USD. Used by image-output models
	 * (e.g. gpt-image-2) where OpenAI bills cached image tokens at a different
	 * rate than cached text tokens. When unset, cached image tokens fall back
	 * to `cachedInputPrice`.
	 */
	cachedImageInputPrice?: Price;
	/**
	 * Price per cached audio input token in USD. Used by Google Gemini models
	 * which list a separate context-cache rate for audio that's higher than the
	 * text/image/video cache rate. When unset, cached audio tokens fall back to
	 * `cachedInputPrice`.
	 */
	cachedInputAudioPrice?: Price;
	/**
	 * Resolution-based token counts for image output.
	 * Maps resolution keys (e.g., "1K", "2K", "4K", "default") to tokens per image.
	 * The per-token price comes from imageOutputPrice.
	 * Use "default" key as a fallback when no imageSize is specified.
	 */
	imageOutputTokensByResolution?: Record<string, number>;
	/**
	 * Resolution-based token counts for image input.
	 * Maps resolution keys (e.g., "1K", "2K", "4K", "default") to tokens per image.
	 * The per-token price comes from imageInputPrice.
	 * Use "default" key as a fallback when no imageSize is specified.
	 */
	imageInputTokensByResolution?: Record<string, number>;
	/**
	 * Price per request in USD
	 */
	requestPrice?: Price;
	/**
	 * Price per page processed in USD for OCR models. Billed against the
	 * `usage_info.pages_processed` count returned by the /v1/ocr endpoint.
	 */
	ocrPagePrice?: Price;
	/**
	 * Price per second in USD for video generation models.
	 * Maps billing keys like "default", "4k", "default_audio", "4k_audio",
	 * "default_video", and "4k_video" to per-second pricing.
	 */
	perSecondPrice?: Record<string, Price>;
	/**
	 * Per-token price in USD for generated video tokens, for video models the
	 * provider bills by output tokens (e.g. Gemini Omni). When the upstream
	 * usage reports video output tokens, finalization bills them at this rate
	 * instead of `perSecondPrice`, which still drives estimates and routing.
	 */
	videoOutputPrice?: Price;
	/**
	 * Price per generated output image in USD, keyed by resolution tier
	 * (e.g. "1K", "2K") with a "default" fallback. For image models whose
	 * provider bills a flat per-image rate that varies with the served
	 * resolution rather than per token (e.g. Alibaba's qwen-image family,
	 * whose usage reports the billed tier in `output_image_type`). Billed
	 * per output image, unlike `requestPrice` which is flat per request.
	 */
	perImagePrice?: Record<string, Price>;
	/**
	 * Pricing tiers for models with context-length based pricing.
	 * When set, inputPrice and outputPrice represent the base tier.
	 * Tiers should be sorted by upToTokens in ascending order.
	 */
	pricingTiers?: PricingTier[];
	/**
	 * Peak/off-peak time-of-day pricing. When present, `peak` applies while the
	 * current UTC hour falls inside `hoursUtc` and `offPeak` applies otherwise.
	 * `offPeakDaysUtc` makes whole UTC days off-peak.
	 */
	peakPricing?: PeakPricing;
	/**
	 * Maximum context window size in tokens
	 */
	contextSize?: number;
	/**
	 * Maximum output size in tokens
	 */
	maxOutput?: number;
	/**
	 * Maximum temperature accepted by this provider mapping.
	 */
	maxTemperature?: number;
	/**
	 * Weight quantization the provider serves this model at (e.g. "fp8").
	 * Only set when the provider explicitly documents the serving precision;
	 * when absent, the quantization is unknown or undisclosed.
	 */
	quantization?: Quantization;
	/**
	 * Whether this specific model supports streaming for this provider.
	 * - true: supports both streaming and non-streaming
	 * - false: does not support streaming
	 * - "only": only supports streaming (non-streaming requests are auto-converted).
	 *   Some providers enforce stream-only for certain models (e.g. Alibaba QwQ series).
	 *   Ref: https://www.alibabacloud.com/help/en/model-studio/stream
	 */
	streaming: boolean | "only";
	/**
	 * Whether this specific model supports vision (image inputs) for this provider
	 */
	vision?: boolean;
	/**
	 * Whether remote image URLs must be fetched by the gateway and inlined as
	 * base64 data URLs before the request goes upstream. Some deployments only
	 * decode a subset of formats when they fetch the URL themselves (e.g.
	 * Novita's ERNIE 4.5 VL endpoint accepts a remote JPEG but rejects a remote
	 * PNG outright, while accepting the very same PNG bytes as a data URL).
	 */
	requiresBase64Images?: boolean;
	/**
	 * Whether this specific model accepts audio inputs (`input_audio` content
	 * blocks) for this provider. Used by the `model: "auto"` router to avoid
	 * selecting providers that would fail upstream when the request contains
	 * audio content.
	 */
	audio?: boolean;
	/**
	 * Whether this specific model accepts document inputs (`file` content
	 * blocks carrying PDF or text-family MIME types) for this provider. Used by
	 * the `model: "auto"` router and capability validator to avoid selecting
	 * providers that would fail upstream when the request contains document
	 * content. Per-provider MIME allowlists live in the provider transform
	 * modules (e.g. transform-google-messages.ts).
	 */
	document?: boolean;
	/**
	 * Whether this model supports reasoning mode
	 */
	reasoning?: boolean;
	/**
	 * Whether this model supports the OpenAI `verbosity` parameter
	 * (low/medium/high response detail control, GPT-5 and later)
	 */
	verbosity?: boolean;
	/**
	 * Whether the provider returns reasoning inside tagged content (e.g. &lt;think&gt;...&lt;/think&gt;)
	 * that needs to be split into separate reasoning and content fields
	 */
	splitTaggedReasoning?: boolean;
	/**
	 * Whether this provider mapping requires an explicit chat-template flag to
	 * produce reasoning. Hybrid models like DeepSeek V3.2 on Novita keep thinking
	 * off by default and ignore `reasoning_effort`, so the gateway sends
	 * `chat_template_kwargs: { thinking: true }` (the documented vLLM/Novita
	 * parameter) when the caller requests reasoning.
	 */
	requiresEnableThinking?: boolean;
	/**
	 * Whether this provider mapping turns thinking off through a separate binary
	 * `thinking: { type: "disabled" }` parameter instead of
	 * `reasoning_effort: "none"`. Together AI serves reasoning models on two
	 * stacks: its gpt-oss/Gemma deployments validate `reasoning_effort` and take
	 * `none` there (sending `thinking` has no effect), while its
	 * DeepSeek/MiniMax/Kimi deployments ignore `reasoning_effort: "none"` and
	 * only honour the `thinking` switch. Only meaningful on mappings that
	 * declare `none` in `reasoningEfforts`.
	 */
	requiresDisableThinkingParam?: boolean;
	/**
	 * Name of the chat-template kwargs key used to control thinking on
	 * mappings that think by default and expose only a chat-template flag
	 * (e.g. vLLM-hosted hybrid models). When set, `reasoning_effort: "none"`
	 * sends `chat_template_kwargs: { [key]: false }` to turn thinking off, and
	 * any other effort sends `{ [key]: true }` — a boolean value under the
	 * named key. Differs from `requiresEnableThinking`, which always sends
	 * `chat_template_kwargs: { thinking: true }`, and
	 * `requiresDisableThinkingParam`, which sends a top-level
	 * `thinking: { type: "disabled" }` object.
	 */
	chatTemplateThinkingKey?: string;
	/**
	 * Whether this model supports the OpenAI responses API (defaults to true if reasoning is true)
	 */
	supportsResponsesApi?: boolean;
	/**
	 * Provider-specific request/endpoint format when a provider has multiple API
	 * surfaces for different models. Defaults to the provider's native format.
	 */
	apiFormat?: ProviderApiFormat;
	/**
	 * API surface for requests carrying a `web_search` tool, when the
	 * provider serves hosted search on a different surface than `apiFormat`
	 * (e.g. DeepSeek: Responses API on V4 Pro, Anthropic Messages API on
	 * Flash). Other requests keep `apiFormat`.
	 */
	webSearchApiFormat?: RequestApiFormat;
	/**
	 * AWS Bedrock OpenAI-format mappings only: the cross-region regions
	 * (`global`, `us`, …) are real inference profiles, served by
	 * bedrock-runtime under the region-prefixed model id. Without it every
	 * region is served in-region by Mantle under the bare id.
	 */
	crossRegionProfiles?: boolean;
	/**
	 * Route this Perplexity mapping to the Agent API (`POST /v1/agent`,
	 * Responses-shaped) instead of Sonar's chat/completions, which Perplexity
	 * retires on 2026-09-27. Per mapping rather than per provider so the
	 * mappings still on Sonar keep working until that date.
	 */
	usesPerplexityAgentApi?: boolean;
	/**
	 * Provider service tier IDs supported by this specific model mapping.
	 * Provider definitions own the tier metadata and default multipliers;
	 * mappings opt in to the subset actually supported by the upstream model.
	 */
	serviceTiers?: string[];
	/**
	 * Optional per-tier multiplier overrides for provider/model combinations whose
	 * tier pricing differs from the provider default while still being expressed
	 * as a multiplier over this mapping's standard token prices.
	 */
	serviceTierMultipliers?: Partial<Record<string, number>>;
	/**
	 * Regions where the mapping supports service tiers. When omitted, the mapping
	 * supports its service tiers across all regions.
	 */
	serviceTierRegions?: string[];
	/**
	 * Whether this provider mapping accepts the OpenAI-style `n` parameter
	 * (multiple completion choices per request) natively. When true, the gateway
	 * forwards `n` to the upstream provider; when false/unset, requests with
	 * `n > 1` are rejected with a 400 error. Only set this for providers that
	 * actually accumulate input tokens once and bill output tokens across all
	 * choices upstream (e.g. OpenAI Chat Completions, Google `candidateCount`).
	 */
	supportsN?: boolean;
	/**
	 * Whether this mapping supports `n > 1` on streaming requests. Only
	 * meaningful when supportsN is true; unset means streaming is allowed.
	 * Google accepts candidateCount on generateContent but rejects it on
	 * streamGenerateContent, so Google mappings set this to false.
	 */
	supportsNStreaming?: boolean;
	/**
	 * Upper bound the upstream enforces for `n` (e.g. Google caps
	 * candidateCount at 8). When unset, only the request-schema cap applies.
	 */
	maxN?: number;
	/**
	 * Controls whether reasoning output is expected from the model.
	 * - undefined: Expect reasoning output if reasoning is true (default behavior)
	 * - "omit": Don't expect reasoning output even if reasoning is true (for models like o1 that
	 *   don't return reasoning content, or adaptive-thinking models that may skip thinking for
	 *   simpler prompts)
	 */
	reasoningOutput?: "omit";
	/** Overrides the provider default for credential-bound reasoning payloads. */
	encryptedReasoning?: boolean;
	/** Responses API reasoning summary mode. Defaults to detailed. */
	reasoningSummary?: "auto" | "detailed";
	/**
	 * Whether this model supports explicit reasoning.max_tokens parameter.
	 * When true, users can specify the exact token budget for reasoning instead of using reasoning_effort levels.
	 * Used by Anthropic, Google, Alibaba, DeepSeek, Z.AI, and Moonshot thinking models.
	 */
	reasoningMaxTokens?: boolean;
	/**
	 * Reasoning/thinking API variant for Anthropic models.
	 * - undefined / "enabled": legacy `thinking: { type: "enabled", budget_tokens }` format (default)
	 * - "adaptive": new `thinking: { type: "adaptive" }` + `output_config.effort` format (Opus 4.7+)
	 */
	reasoningMode?: "enabled" | "adaptive";
	/**
	 * Exact `reasoning_effort` values this provider mapping supports, in
	 * ascending order of effort. Effort tiers differ per model generation
	 * (e.g. GPT-5 accepts `minimal`..`high`, GPT-5.6 accepts `none`..`max`,
	 * Anthropic thinking models accept `low`..`max`), so each mapping declares
	 * its own list. The gateway forwards effort values to the provider as-is
	 * and excludes mappings that declare an incompatible effort during automatic
	 * provider selection. The models APIs expose the list for clients. When unset, the
	 * supported values are not (yet) declared for this mapping.
	 */
	reasoningEfforts?: ReasoningEffort[];
	/**
	 * Exact `reasoning.mode` values this provider mapping supports. Only
	 * OpenAI's Responses API documents this parameter, and only for the GPT-5.6
	 * family; every other deployment rejects an unknown `reasoning.mode`, so a
	 * mapping that does not declare it makes the gateway reject the request
	 * rather than drop the field on the way upstream. When unset, the mapping
	 * accepts no explicit mode.
	 */
	reasoningModes?: ReasoningMode[];
	/**
	 * Whether this specific model supports tool calling for this provider
	 */
	tools?: boolean;
	/**
	 * Whether this model supports parallel tool calls
	 */
	parallelToolCalls?: boolean;
	/**
	 * SOFT JSON output (models API: `json_output`): the model can be nudged
	 * into emitting JSON (response_format json_object / prompt guidance).
	 * There is no server-side schema guarantee — off-schema or malformed JSON
	 * is possible and must be caught by the consumer's parser.
	 */
	jsonOutput?: boolean;
	/**
	 * Whether JSON-mode streaming output for this provider mapping should be
	 * buffered and healed before being sent downstream. Use this for providers
	 * that support JSON mode but may stream reasoning or explanatory text as
	 * content before the final JSON object.
	 */
	healStreamingJsonOutput?: boolean;
	/**
	 * STRICT JSON output schema (models API: `structured_outputs`): the
	 * UPSTREAM PROVIDER enforces schema-guided decoding (e.g. OpenAI
	 * structured outputs, vLLM guided decoding). Declare true ONLY when the
	 * true upstream provider natively supports it. The gateway MUST NOT
	 * emulate schema enforcement (no prompt+validate adapter); when false,
	 * requests with response_format: json_schema are rejected with a 400
	 * ("does not support JSON schema output mode").
	 */
	jsonOutputSchema?: boolean;
	/**
	 * Whether this model supports web search/grounding capabilities
	 */
	webSearch?: boolean;
	/**
	 * Price per web search query in USD (charged when web search is used)
	 */
	webSearchPrice?: Price;
	/**
	 * Whether this mapping's upstream can *only* search when the caller forces
	 * it, because it has no model-elected search to fall back on.
	 *
	 * DashScope's `enable_search` is documented as a hint the model may act on,
	 * but on the Qwen models mapped here it never fires — even "what is the
	 * current price of Bitcoin?" comes back at an unchanged prompt size with the
	 * model stating it has no live access. Only `search_options.forced_search`
	 * actually retrieves, and that searches on every single call.
	 *
	 * Neither half is a sane default: forcing bills a search (plus ~2k tokens of
	 * injected snippets) on turns that never needed one, which is what a chat UI
	 * with a "web search" toggle left on would do to every follow-up message,
	 * and not forcing returns confidently stale answers while still occupying
	 * the route that a genuinely search-capable provider would have served.
	 *
	 * So mappings with this flag are only eligible for a request that forces
	 * search via `tool_choice: {type: "web_search"}`. A plain `web_search` tool
	 * with `tool_choice: "auto"` routes elsewhere instead.
	 */
	webSearchForcedOnly?: boolean;
	/**
	 * Wire format the upstream's Chat Completions endpoint takes for web search.
	 * When set, searches are forwarded in this shape, `webSearchForcedOnly` is
	 * ignored (the model elects its own searches), and a search is billed only
	 * on evidence in the response.
	 */
	webSearchFormat?: WebSearchFormat;
	/**
	 * Price per content filter violation in USD (charged additionally when the
	 * provider rejects a request for safety/usage-policy reasons, e.g. xAI's
	 * "Content violates usage guidelines" response).
	 */
	contentFilterPrice?: number;
	/**
	 * List of supported API parameters for this model/provider combination
	 */
	supportedParameters?: string[];
	/**
	 * Which `tool_choice` modes the upstream accepts for this mapping. When
	 * omitted or empty, all modes are assumed supported. When set, a requested
	 * `tool_choice` whose mode is not listed is downgraded to "auto" so
	 * forced-tool requests still succeed instead of erroring upstream.
	 * Modes: "auto", "none", "required", "function" (a named function choice).
	 */
	supportedToolChoices?: ToolChoiceMode[];
	/**
	 * Additional `tool_choice` modes the upstream only accepts while thinking is
	 * turned off, i.e. for requests sending `reasoning_effort: "none"` to a
	 * mapping that publishes `none` in `reasoningEfforts`. These modes are added
	 * to `supportedToolChoices` for such requests and ignored for every other
	 * one. Only meaningful alongside a non-empty `supportedToolChoices`.
	 */
	supportedToolChoicesWithThinkingDisabled?: ToolChoiceMode[];
	/**
	 * Whether this mapping's upstream accepts the OpenAI-only `developer` message
	 * role. Defaults to `true` (assumed supported). When set to `false`, the
	 * gateway rewrites `developer` messages to `system` before forwarding, since
	 * some OpenAI-compatible providers reject `developer` with a 400 ("developer
	 * is not one of ['system', 'assistant', 'user', 'tool', 'function']").
	 */
	supportsDeveloperRole?: boolean;
	/**
	 * Whether this mapping's upstream accepts a conversation whose last message is
	 * an assistant turn (assistant prefill / continuation). Defaults to `true`
	 * (assumed supported). When set to `false`, routing skips this mapping for
	 * requests that end on an assistant message, since the upstream rejects them
	 * with a 400 ("a conversation cannot end on an assistant turn").
	 */
	supportsAssistantPrefill?: boolean;
	/**
	 * Whether this mapping's upstream accepts `system` messages inside the
	 * Anthropic `messages` array (Claude Opus 4.8 and later). When unset, a
	 * system message that arrives mid-conversation is sent in place as a user
	 * `<system-reminder>` instead.
	 */
	midConversationSystem?: boolean;
	/**
	 * Test skip/only functionality
	 */
	test?: "skip" | "only";
	/**
	 * Stability level of the model for this specific provider (defaults to model-level stability if not specified)
	 * - stable: Fully tested and production ready
	 * - beta: Generally stable but may have minor issues
	 * - unstable: May have significant issues or frequent changes
	 * - experimental: Early stage, use with caution
	 */
	stability?: StabilityLevel;
	/**
	 * Date when the model mapping will be deprecated (still usable but filtered from selection algorithms)
	 */
	deprecatedAt?: Date;
	/**
	 * Date when the model mapping will be deactivated (returns error when requested)
	 */
	deactivatedAt?: Date;
	/**
	 * Whether this model uses a dedicated image generation API.
	 * When true, requests are routed to a provider-specific image generation endpoint.
	 */
	imageGenerations?: boolean;
	/**
	 * Whether this model uses a dedicated embeddings API.
	 * When true, requests are routed to a provider-specific /v1/embeddings endpoint
	 * and pricing is computed against input tokens only (no completion tokens).
	 */
	embeddings?: boolean;
	/**
	 * Whether this model uses a dedicated speech generation API.
	 * When true, requests are routed to the gateway's /v1/audio/speech endpoint
	 * which returns binary audio rather than a chat completion.
	 */
	speechGenerations?: boolean;
	/**
	 * Whether this model uses the dedicated realtime WebSocket API.
	 * When true, sessions are served by the gateway's /v1/realtime endpoint
	 * (server-to-server WebSocket proxy) rather than /v1/chat/completions.
	 * Pricing uses the modality-specific token prices on this mapping
	 * (inputPrice/cachedInputPrice/outputPrice for text, inputAudioPrice/
	 * cachedInputAudioPrice/outputAudioPrice for audio, imageInputPrice/
	 * cachedImageInputPrice for image input).
	 */
	realtime?: boolean;
	/**
	 * Whether this mapping can transcribe input audio for realtime sessions.
	 * When true, the gateway's /v1/realtime proxy allows this mapping as the
	 * `input_audio_transcription.model` of a realtime session and bills each
	 * `conversation.item.input_audio_transcription.completed` event against
	 * this mapping: token usage via inputPrice (text), inputAudioPrice (audio)
	 * and outputPrice, or duration usage via inputAudioHourPrice. A mapping
	 * must declare the prices matching the usage shape its provider reports.
	 * Also makes the mapping connectable as a transcription-only session
	 * (`/v1/realtime?intent=transcription`).
	 */
	realtimeTranscription?: boolean;
	/**
	 * Whether this mapping accepts a `turn_detection` config on a transcription
	 * session. Streaming ASR deployments segment continuously on their own and
	 * reject the field outright, so it is opt-in: without it, callers must
	 * commit turns themselves (`turn_detection: null` plus
	 * `input_audio_buffer.commit`).
	 */
	realtimeTranscriptionTurnDetection?: boolean;
	/**
	 * Whether this model uses a dedicated transcription (speech-to-text) API.
	 * When true, requests are routed to the gateway's /v1/audio/transcriptions
	 * endpoint, which turns audio into text rather than returning a chat
	 * completion. Billed on audio duration via inputAudioHourPrice.
	 */
	transcriptions?: boolean;
	/**
	 * Whether this model uses a dedicated OCR (optical character recognition)
	 * API. When true, requests are routed to the gateway's /v1/ocr endpoint,
	 * which extracts text/markdown from documents and images rather than
	 * returning a chat completion. Billed per page processed via ocrPagePrice.
	 */
	ocr?: boolean;
	/**
	 * Whether this model uses a dedicated rerank API.
	 * When true, requests are routed to the gateway's /v1/rerank endpoint.
	 */
	rerank?: boolean;
	/**
	 * Whether this model uses a dedicated typed-decision API (OpenAI Decisions,
	 * TypeSafe System One). When true, requests are routed to the gateway's
	 * /v1/decisions endpoint (TypeSafe models also on the legacy /v1/systemone),
	 * which answers typed questions with probabilities instead of generated
	 * text. Billed on input tokens only.
	 */
	decisions?: boolean;
	/**
	 * Whether this model uses a dedicated web search API that returns ranked
	 * results instead of generated text. When true, requests are routed to the
	 * gateway's /v1/search endpoint and billed per request via requestPrice.
	 */
	search?: boolean;
	/**
	 * Prebuilt voices supported for speech generation models. The first entry is
	 * used as the default when the caller does not specify a `voice`.
	 */
	supportedVoices?: string[];
	/**
	 * Geographic region for this provider mapping.
	 * Set automatically when a mapping with `regions` is expanded into flat entries.
	 * When absent (undefined), the provider uses a single global endpoint.
	 */
	region?: string;
	/**
	 * Available regions for this provider mapping.
	 * Each region can optionally override pricing and other properties.
	 * Properties not specified in a region entry are inherited from the parent mapping.
	 * At sync/routing time, each region is expanded into a separate DB row / candidate.
	 */
	regions?: ProviderRegion[];
	/**
	 * The region-less root of this mapping is itself a real, routable
	 * deployment (the provider's default endpoint) rather than a synthetic
	 * aggregate of its regions, so routing keeps it as a candidate alongside
	 * the regional variants. Set on Airside listings, whose regional fares are
	 * add-ons to the default deployment.
	 */
	routableRoot?: boolean;
	/**
	 * Whether this model uses a dedicated video generation API.
	 * When true, requests are routed to a provider-specific video generation endpoint.
	 */
	videoGenerations?: boolean;
	/**
	 * Supported OpenAI-style video sizes in widthxheight format for this provider.
	 */
	supportedVideoSizes?: string[];
	/**
	 * Supported output durations in seconds for this provider.
	 */
	supportedVideoDurationsSeconds?: number[];
	/**
	 * Supported output durations in seconds when using image-to-video (frame inputs).
	 * Overrides supportedVideoDurationsSeconds for that input mode when set.
	 */
	supportedVideoDurationsSecondsImageToVideo?: number[];
	/**
	 * Whether this provider mapping supports generating video with audio.
	 */
	supportsVideoAudio?: boolean;
	/**
	 * Whether this provider mapping supports generating video without audio.
	 */
	supportsVideoWithoutAudio?: boolean;
	/**
	 * The output is as long as the input audio rather than the requested
	 * `seconds`, so per-second billing uses the measured output duration.
	 */
	videoDurationFollowsAudio?: boolean;
}

export type StabilityLevel = "stable" | "beta" | "unstable" | "experimental";

export type Quantization =
	"int4" | "int8" | "fp4" | "fp6" | "fp8" | "fp16" | "bf16" | "fp32";

export interface ModelDefinition {
	/**
	 * Unique identifier for the model
	 */
	id: string;
	/**
	 * Human-readable display name for the model
	 */
	name?: string;
	/**
	 * Alternative names or search terms for the model
	 */
	aliases?: string[];
	/**
	 * Model family (e.g., 'openai', 'deepseek', 'anthropic')
	 */
	family: string;
	/**
	 * Mappings to provider models
	 */
	providers: ProviderModelMapping[];
	/**
	 * Whether this model is free to use
	 */
	free?: boolean;
	/**
	 * Only organizations with enterprise access (KYC'd) may request this
	 * model; everyone else gets a 403 and auto routing never selects it.
	 */
	enterpriseOnly?: boolean;
	/**
	 * Rate limit tier for free models (defaults to 'low' if not specified)
	 * - low: Standard rate limits for free models
	 * - high: More generous rate limits for free models
	 * Only applies when free is true
	 */
	rateLimitKind?: "low" | "high";
	/**
	 * Output formats supported by the model (defaults to ['text'] if not specified)
	 */
	output?: (
		| "text"
		| "image"
		| "video"
		| "embedding"
		| "audio"
		| "ocr"
		| "transcription"
		| "rerank"
		| "decision"
		| "search"
	)[];
	/**
	 * Whether this model requires an image input to function (e.g. image editing models).
	 */
	imageInputRequired?: boolean;
	/**
	 * Maximum supported output duration in seconds for video generation models.
	 */
	maxVideoDurationSeconds?: number;
	/**
	 * Stability level of the model (defaults to 'stable' if not specified)
	 * - stable: Fully tested and production ready
	 * - beta: Generally stable but may have minor issues
	 * - unstable: May have significant issues or frequent changes
	 * - experimental: Early stage, use with caution
	 */
	stability?: StabilityLevel;
	/**
	 * Whether this model supports system role messages (defaults to true if not specified)
	 */
	supportsSystemRole?: boolean;
	/**
	 * Description of the model
	 */
	description?: string;
	/**
	 * Date when the model was released by the provider
	 */
	releasedAt?: Date;
	/** Aliases whose served model version may change. */
	aliasesAreVirtual?: boolean;
}

export const models = [
	...llmgatewayModels,
	...openaiModels,
	...anthropicModels,
	...googleModels,
	...inclusionaiModels,
	...perplexityModels,
	...xaiModels,
	...xiaomiModels,
	...metaModels,
	...deepseekModels,
	...mistralModels,
	...microsoftModels,
	...minimaxModels,
	...moonshotModels,
	...alibabaModels,
	...atlascloudModels,
	...atriaModels,
	...baaiModels,
	...baiduModels,
	...blackForestLabsModels,
	...bytedanceModels,
	...nousresearchModels,
	...reveModels,
	...sakanaModels,
	...kinfraModels,
	...tencentModels,
	...nvidiaModels,
	...openbmbModels,
	...zaiModels,
	...elevenlabsModels,
	...typesafeModels,
	...thinkingmachinesModels,
	...stepfunModels,
	...ibmModels,
] as const satisfies ModelDefinition[];
